import { Skeleton } from "@/components/ui/skeleton";
import { apiBaseUrl } from "@/shared/api/apiBaseUrl";
import type { PresentatorItem } from "./ProfileView/types";
import type { LocalAudioRecord, ProfileMeta } from "../types";
import {
  ProfileView,
  ProfileCreateForm,
  ProfileProcessingCard,
  ProfileErrorBanner,
  AudioRecordsList,
} from "./ProfileView";

export type ProfileWorkspaceBodyProps = {
  selectedProfileId: string;
  selectedMeta: ProfileMeta | undefined;
  username: string;
  localRecords: LocalAudioRecord[];
  onUpdateFormItem: (args: {
    profileId: string;
    username: string;
    telephoneE164: string;
    key?: string | null;
    item: PresentatorItem;
  }) => Promise<void>;
  onStartCreate: (telephone: string) => void;
  onDismissError: () => void;
  onRenameLocalRecord: (args: {
    profileId: string;
    localId: string;
    name: string;
  }) => void;
  onDeleteLocalRecord: (args: { profileId: string; localId: string }) => void;
  onRetryUploadLocalRecord: (args: {
    profileId: string;
    localId: string;
  }) => void;
  prefillTelephone?: string;
  registerTourTarget?: (id: string, el: HTMLElement | null) => void;
  scrollToRecordId?: string | null;
};

export function ProfileWorkspaceBody({
  selectedProfileId,
  selectedMeta,
  username,
  localRecords,
  onUpdateFormItem,
  onStartCreate,
  onDismissError,
  onRenameLocalRecord,
  onDeleteLocalRecord,
  onRetryUploadLocalRecord,
  prefillTelephone,
  registerTourTarget,
  scrollToRecordId,
}: ProfileWorkspaceBodyProps) {
  return (
    <>
      {selectedMeta?.isLoadingProfileData ||
      (selectedMeta?.status === "success" &&
        !selectedMeta?.profileData &&
        !selectedMeta?.hasLoadedProfileData) ? (
        <div className="space-y-6">
          <Skeleton className="h-56 w-full rounded-l" />
        </div>
      ) : (selectedMeta?.status === "success" ||
          (!selectedMeta?.status && selectedMeta?.profileData)) &&
        selectedMeta?.profileData ? (
        <ProfileView
          profileData={selectedMeta.profileData}
          username={username}
          profileId={selectedProfileId}
          onUpdateFormItem={onUpdateFormItem}
          registerTourTarget={registerTourTarget}
        />
      ) : selectedMeta?.status === "processing" ? (
        selectedMeta?.profileData ? (
          <div className="space-y-4">
            <ProfileProcessingCard
              telephone={selectedMeta.telephone}
            />
            <ProfileView
              profileData={selectedMeta.profileData}
              username={username}
              profileId={selectedProfileId}
              onUpdateFormItem={onUpdateFormItem}
              registerTourTarget={registerTourTarget}
            />
          </div>
        ) : (
          <ProfileProcessingCard
            telephone={selectedMeta.telephone}
          />
        )
      ) : selectedMeta?.status === "error" ? (
        <div className="space-y-4 mb-8">
          <ProfileErrorBanner
            message={
              selectedMeta.errorMessage ||
              "Не удалось сформировать анкету профиля. Повторите попытку."
            }
            onDismiss={onDismissError}
          />
          {selectedMeta.profileData &&
          selectedMeta.profileData.forms.length > 0 ? (
            <ProfileView
              profileData={selectedMeta.profileData}
              username={username}
              profileId={selectedProfileId}
              onUpdateFormItem={onUpdateFormItem}
              registerTourTarget={registerTourTarget}
            />
          ) : null}
        </div>
      ) : (
        <div className="rounded-l border p-6 mb-8">
          <ProfileCreateForm
            disabled={Boolean(selectedMeta?.isLoadingProfileData)}
            onSubmit={onStartCreate}
            prefillTelephone={prefillTelephone}
            registerTourTarget={registerTourTarget}
          />
        </div>
      )}

      <AudioRecordsList
        remoteRecords={selectedMeta?.profileData?.records ?? []}
        localRecords={localRecords}
        profileId={selectedProfileId}
        baseUrl={apiBaseUrl()}
        onRenameLocalRecord={onRenameLocalRecord}
        onDeleteLocalRecord={onDeleteLocalRecord}
        onRetryUploadLocalRecord={onRetryUploadLocalRecord}
        scrollToRecordId={scrollToRecordId}
        registerTourTarget={registerTourTarget}
      />
    </>
  );
}
